import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api-errors';
import { uploadBlob } from '@/lib/upload';

export const GivingLinkImageField = ({
  imageKey,
  previewUrl,
  onChange,
  idPrefix,
}: {
  imageKey: string | null;
  previewUrl: string | null;
  onChange: (next: { imageKey: string | null; previewUrl: string | null }) => void;
  idPrefix: string;
}) => {
  const { t } = useTranslation('payments');
  const { t: tErrors } = useTranslation('errors');
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <Label htmlFor={`${idPrefix}-image`}>{t('links.form.image')}</Label>
      <p className="mb-2 text-caption text-fg-secondary">{t('links.form.imageHint')}</p>
      {previewUrl ? (
        <div className="flex items-center gap-3">
          <img
            src={previewUrl}
            alt=""
            className="size-20 shrink-0 rounded-lg object-cover"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange({ imageKey: null, previewUrl: null })}
          >
            {t('links.form.imageRemove')}
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          id={`${idPrefix}-image`}
          onClick={() => inputRef.current?.click()}
        >
          {t('links.form.imageAdd')}
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          void (async () => {
            try {
              const { key } = await uploadBlob({ purpose: 'content_image', blob: file });
              onChange({
                imageKey: key,
                previewUrl: URL.createObjectURL(file),
              });
            } catch (err) {
              toast.error(
                err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'),
              );
            }
          })();
        }}
      />
      {imageKey && !previewUrl ? (
        <p className="mt-1 text-caption text-fg-secondary">{t('links.form.imageSaved')}</p>
      ) : null}
    </div>
  );
};
